<script>
import { defineComponent, markRaw } from 'vue'
import { http } from '@/plugins/http'
import moment from 'moment'
import AgGridMethods from '@/Mixins/SigmaClientNew/AgGridMethods'
import AgGridTransactionsColumns from '@/Mixins/SigmaClientNew/AgGridTransactionsColumns'
import { AgGridVue } from 'ag-grid-vue3'
import AgGridBtnRender from '@/components/SigmaClientNew/AgGridBtnRender'
import AgGridSearchFilter from '@/components/SigmaClientNew/AgGridSearchFilter'
import AgGridSearchFloatingFilter from '@/components/SigmaClientNew/AgGridSearchFloatingFilter'
import AgGridTextFloatingFilter from '@/components/SigmaClientNew/AgGridTextFloatingFilter'
import { mapGetters } from 'vuex'
import { getServerFilterConfig, GRID_FILTER_PARAMS, PAYMENT_STATUS_VALUES, DEFAULT_UNTICKED_VALUES, SEEDED_FILTER_COLUMNS, statusFilterValues, includesVoidedSourceCodes, offeredSourceCodes, pickedValues, saveFilterModel, readFilterModel, clearFilterModel } from '@/Mixins/SigmaClientNew/transactionGridFilters'

export default defineComponent({
  name: 'TransactionsConfList',

  components: {
    AgGridVue
  },
  mixins: [ AgGridMethods, AgGridTransactionsColumns ],

  props: {
    // Name of input field.  Not really necessary, but helpful if we use the form object down the road
    name: {
      required: true,
      type: String
    },

    iteration: {
      required: false,
      type: Number,
      default: 0
    },

    classes: {
      required: false,
      type: String,
      default: 'ag-theme-balham'
    },

    activeTab: {
      required: false,
      type: String,
      default: ''
    },

    rows: {
      required: true
    },

    // Ag-grid column defs
    columns: {
      required: false,
      type: Array,
      default: undefined
    },

    // Ag-grid Options
    options: {
      required: false,
      type: Object,
      default: undefined
    },
    aggridColumns: {
      required: false,
      type: Array
    },

    /**
     * Criteria the view itself owns rather than the column filters, e.g. the statuses of the
     * selected tab or the payroll source code. They are the base of every server side filter query
     * and the fallback whenever a column filter is cleared.
     */
    baseQuery: {
      required: false,
      type: Object,
      default: () => ({})
    },
    emitSelect: {
      required: false,
      type: Boolean,
      default: false
    },
    loadingRelated: {
      required: false,
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      isError: false, // If the input or selected field is incorrect, show an error
      isLoading: false,
      gridApi: null,
      columnApi: null,
      defaultColDefs: undefined,
      defaultGridOptions: undefined,
      changes: [],
      components: null,
      rowHeight: 40,
      domLayout: 'autoHeight',
      filterDebounceTimer: null,
      /**
       * Model writes the grid was given rather than filters the user picked. Each one comes back as
       * a filter change of its own, which is not the user filtering - the rows on screen already
       * answer to it - so it is counted here and swallowed when it arrives. ag-grid applies a set
       * filter once its values callback has run, which it does on a timer, so the change can come
       * long after the write: only the event itself can clear this, never a tick of the clock.
       */
      quietFilterWrites: 0,
      lastFilterModel: null,
      defaultFiltersSeeded: false,
      // Set when the filters are started over, for the one load that follows: it asks with the
      // view's own criteria, since the filters hold nothing of the user's to be read for
      filtersStartedOver: false,
      // What the view is asking for besides the column filters, i.e. its keyword search. Kept so
      // the filters can be read against it.
      extendedFilters: {},
      // Whether the source code filter was asking for the journals held as voided ones the last
      // time the list was loaded, so the status filter only follows it when that actually changes
      voidedIncluded: false
    }
  },

  computed: {
    ...mapGetters({
      currentApsPage: 'getCurrentApPage',
      currentPettycashPage: 'getCurrentPcPage',
      currentJesPage: 'getCurrentJePage',
      currentPosPage: 'getCurrentPoPage',
      selection: 'getSelectedTransactions',
      getCurrencyByBatchId: 'getCurrencyByBatchId',
      getFilters: 'getFilters',
      isFilterLoading: 'getTransactionFilterStatus'
    }),
    currentPage () {
      const routeName = {
        'APs': 'currentApsPage',
        'PCs': 'currentPettycashPage',
        'JEs': 'currentJesPage',
        'PRs': 'currentJesPage',
        'POs': 'currentPosPage'
      }
      return this[routeName[this.$route.name]]
    },
    /**
     * The actions used to refresh this view, or null when the route still filters client side
     */
    serverFilterConfig () {
      return getServerFilterConfig(this.$route.name)
    },
    columnDefs () {
      if (this.columns && Array.isArray(this.columns)) {
        return this.columns
      } else {
        return this.defaultColDefs
      }
    },
    gridOptions () {
      return this.options || this.defaultGridOptions
    },
    width () {
      return `calc(100vw - 200px)`
    }
  },

  methods: {
    // Ag-grid
    onGridReady (params) {
      if (!params) return
      this.gridApi = params.api
      this.columnApi = params.api
      // v31: set the initial rows through the grid option (setRowData is removed); the `rows` watcher keeps
      // them in sync and clears the No-Rows overlay on a refetch.
      this.setGridRows(this.rows)
      if (this.serverFilterConfig || this.isFilterLoading) this.gridApi.showLoadingOverlay()
      // This grid's own element, not the first one on the page: a view with two grids would
      // otherwise have both of them listening on the first grid's cells
      if (this.$el) {
        this.$el.addEventListener('click', this.handleGridClick)
      }
      // Loading the list is the grid's: where the user left off on a return trip from a transaction
      // - their filters and the page they were reading - or the head of the list on the view's own
      // criteria, with the defaults its filters open on
      const saved = this.savedFilterState()
      // Settled before the filters are seeded, since what the view asks for decides what they open
      // on. Nothing is being ticked or unticked here, so the status filter is left as it is restored.
      this.extendedFilters = saved.extendedFilters || {}
      this.voidedIncluded = this.voidedSourceCodesSelected(saved.model)
      /**
       * The list is asked for on the model the filters were just given, not on the one the grid can
       * be read for: the set filters are handed their values through a callback, which ag-grid only
       * calls on a timer, so what was put in has not reached them yet. Reading the grid here would
       * be reading filters that hold nothing - and asking for what nothing stands for, every value
       * ticked, which is not what the view opens on.
       */
      const model = this.restoreSavedFilterModel(saved) ? saved.model : this.seedDefaultFilters()
      this.loadRows(saved.page, model, saved.extendedFilters || {})
    },
    /**
     * Fetches a page of the list with whatever the column filters currently hold. Every load goes
     * through here: entering the view, paging, and changing tab.
     * @param page
     * @param filterModel   The filters to ask with, when they have just been applied to the grid and
     *                      cannot be read back from it yet. Defaults to what the grid holds.
     */
    loadRows (page = 1, filterModel = null, extendedFilters = {}) {
      if (!this.gridApi || !this.serverFilterConfig) return
      this.extendedFilters = extendedFilters || {}
      // The one load after the filters were started over, e.g. a tab change: the tab's own statuses
      // are what it asks for, and the filters are left out of the query altogether
      if (this.filtersStartedOver) {
        this.filtersStartedOver = false
        this.voidedIncluded = false    // Nothing is ticked to be asking for the voided journals
        return this.fetchFilteredRows(this.buildFilterQuery({}, page, extendedFilters, true), {})
      }
      const model = this.syncVoidedStatus(this.activeFilterModel(filterModel || this.gridApi.getFilterModel()))
      return this.fetchFilteredRows(this.buildFilterQuery(model, page, extendedFilters), model)
    },
    /**
     * The source codes the filter is asking for, which is not the same as what its model holds:
     * every value ticked leaves no model behind, and that is still asking for all of them
     * @param filterModel   ag-grid filter model
     * @return {boolean}    Whether the journals held as voided ones are among them
     */
    voidedSourceCodesSelected (filterModel) {
      return includesVoidedSourceCodes(this.filterSelection(filterModel, 'source_code') || [])
    },
    /**
     * Follows the source code filter's DC/reversal values with the status filter's VOIDED value,
     * which is what those journals are held as: ticking either ticks VOIDED, unticking both unticks
     * it again. Only the moment one of them is ticked or unticked moves it - paging and searching
     * leave it alone - so from there the user owns the tick, and the query asks for whatever they
     * leave it at.
     * @param filterModel   ag-grid filter model
     * @return {Object}     The model to filter with
     */
    syncVoidedStatus (filterModel) {
      const included = this.voidedSourceCodesSelected(filterModel)
      if (included === this.voidedIncluded) return filterModel
      this.voidedIncluded = included
      if (!this.columnApi || !this.columnApi.getColumn('status')) return filterModel
      const values = this.statusSelectionWithVoided(filterModel, included)
      if (!values) return filterModel
      const status = (filterModel && filterModel.status) || { filterType: 'set' }
      const updated = Object.assign({}, filterModel, {
        status: Object.assign({}, status, { values })
      })
      // The rows about to be fetched are the answer to this, so putting it in filters nothing again
      this.setFilterModelQuietly(updated)
      return updated
    },
    /**
     * The status filter's values with VOIDED put in or taken out, or null when it already reads that
     * way. A filter with no model of its own has every status ticked, VOIDED among them, so it only
     * has to be written out when VOIDED is the one being taken out.
     * @param filterModel   ag-grid filter model
     * @param included      Whether the view is asking for voided transactions
     * @return {Array|null}
     */
    statusSelectionWithVoided (filterModel, included) {
      const offered = this.filterValues('status')
      if (!offered.includes('VOIDED')) return null
      const status = filterModel && filterModel.status
      const selected = (status && Array.isArray(status.values)) ? status.values : null
      if (included) {
        return (!selected || selected.includes('VOIDED')) ? null : selected.concat(['VOIDED'])
      }
      const current = selected || this.filterValues('status')
      return current.includes('VOIDED') ? current.filter(value => value !== 'VOIDED') : null
    },
    /**
     * Puts a model into the grid without the change filtering again, for the criteria the rows on
     * screen already answer to. Every write the grid is given goes through here, so the change it
     * comes back as is known for the grid's own and not the user picking filters.
     * @param filterModel   ag-grid filter model
     */
    setFilterModelQuietly (filterModel) {
      this.quietFilterWrites += 1
      this.gridApi.setFilterModel(filterModel)
      /**
       * Clearing the filters is the one write no change need come back for: ag-grid waits on the
       * filters it reset, and a grid whose filters have not been built yet has none to wait on -
       * the count would be left standing and would swallow the next change the user makes. The
       * change a cleared filter does raise comes back while the model is being set, so by here it
       * has been counted off already.
       */
      if (!(filterModel && Object.keys(filterModel).length)) this.quietFilterWrites = 0
    },
    handleGridClick (event) {
      const target = event.target
      if (target.classList.contains('clickable-link')) {
        const path = target.dataset.path // Extract the path from the dataset
        // The row being opened is one of the list as it stands, so where the user is reading it is
        // kept for the way back. Leaving by any other route keeps nothing, and the list starts clean.
        this.saveCurrentFilterModel()
        this.$router.push(path) // Navigate programmatically using Vue Router
      }
    },
    /**
     * Keeps the filters the list is showing and the page it is on, for the return trip from a
     * transaction
     */
    saveCurrentFilterModel () {
      if (!this.gridApi || !this.serverFilterConfig) return
      const filterModel = this.activeFilterModel(this.gridApi.getFilterModel())
      const saved = readFilterModel(this.$route.name) || {}
      saveFilterModel(this.$route.name, filterModel, this.currentPage || 1, saved.extendedFilters || {})
    },
    onFirstDataRendered (params) {
      if (!params) return
      this.safeSizeColumnsToFit(params)
      this.seedDefaultFilters()
    },
    /**
     * Where the list was left on the way into a transaction: the column filters it was showing and
     * the page they were read on. Nothing kept - which is every arrival other than a return trip,
     * settled on entry before this grid was built - is the head of the list with no filters.
     * @return {Object}   { model, page }
     */
    savedFilterState () {
      const saved = readFilterModel(this.$route.name) || {}
      const model = this.activeFilterModel(saved.model)
      return { model: Object.keys(model).length ? model : null, page: saved.page || 1, extendedFilters: saved.extendedFilters || {} }
    },
    /**
     * Puts back the filters the list was left with on the way into a transaction, so the rows that
     * are about to be fetched are read under the criteria they answer to
     * @param saved       What savedFilterState found
     * @return {boolean}  Whether there were filters to put back
     */
    restoreSavedFilterModel (saved) {
      const filterModel = saved && saved.model
      if (!this.gridApi || !this.serverFilterConfig || !filterModel) return false
      // The user's own filters, so the defaults have nothing left to seed over
      this.defaultFiltersSeeded = true
      this.setFilterModelQuietly(filterModel)
      return true
    },
    /**
     * Starts the set filters with the values the view does not ask for unticked - DC and reversal
     * journals, deleted payments - which is what the rows already on screen are. Seeding only sets
     * what the filters read: nothing is fetched, since the rows already answer to it.
     * @return {Object|null}  The model the filters were given, which a query has to be built from
     *                        rather than read back from the grid, or null when nothing was seeded
     */
    seedDefaultFilters () {
      if (this.defaultFiltersSeeded || !this.gridApi || !this.columnApi) return null
      const filterModel = this.gridApi.getFilterModel() || {}
      const seeded = Object.assign({}, filterModel)
      let seededAny = false
      let valuesPending = false

      // The source codes are seeded before the statuses, since whether the DC and reversal ones are
      // among them decides whether the status filter opens on VOIDED
      SEEDED_FILTER_COLUMNS.forEach(columnId => {
        // Only where the view has the column, and only while the user has not filtered on it
        if (!this.columnApi.getColumn(columnId) || filterModel[columnId]) return
        // The banks and source codes are fetched in their own right, so they can still be on the way
        if (!this.filterValues(columnId).length) {
          valuesPending = true
          return
        }
        const ticked = this.defaultFilterSelection(columnId, seeded)
        if (!ticked) return
        seeded[columnId] = { filterType: 'set', values: ticked }
        seededAny = true
      })

      // Nothing waiting on data means this is as seeded as the view gets, and the user owns the
      // filters from here on
      if (!valuesPending) this.defaultFiltersSeeded = true
      if (!seededAny) return null
      this.defaultFiltersSeeded = true
      this.setFilterModelQuietly(seeded)
      return seeded
    },
    /**
     * The values a set filter starts ticked, or null when it starts on all of them and there is
     * nothing to seed. What the rows on screen already answer to, never a new set of criteria.
     * @param columnId
     * @param filterModel   The filters the column is read against, for the ones that depend on
     *                      another: the statuses on the source codes being asked for
     * @return {Array|null}
     */
    defaultFilterSelection (columnId, filterModel = null) {
      const values = this.filterValues(columnId)
      if (!values.length) return null    // The values are not loaded yet, so there is nothing to tick

      const unticked = this.untickedValues(columnId, filterModel)
      if (!unticked) return null
      const ticked = values.filter(value => !unticked.includes(value))
      // Everything ticked is what a filter does anyway, so it is left alone
      if (ticked.length === values.length) return null
      // The blank a filter offers is left out of this, so a column the rows are not all filled in
      // for opens with it unticked - like the values above, it is the user's to ask for
      return ticked
    },
    /**
     * The values a set filter starts unticked, less the ones the filters are already asking for:
     * with the DC or reversal source codes ticked the status filter opens on VOIDED ticked, since
     * that is what those journals are.
     * @param columnId
     * @param filterModel   The filters the column is read against
     * @return {Array|null}
     */
    untickedValues (columnId, filterModel = null) {
      const unticked = DEFAULT_UNTICKED_VALUES[columnId]
      if (!unticked) return null
      if (columnId !== 'status' || !this.voidedSourceCodesSelected(filterModel)) return unticked
      const kept = unticked.filter(value => value !== 'VOIDED')
      // Nothing left out is what the filter does on its own, so there is nothing to seed
      return kept.length ? kept : null
    },
    /**
     * Every value a set filter offers, which is also everything it is asking for while it is
     * absent from the model
     * @param columnId
     * @return {Array}
     */
    filterValues (columnId) {
      if (columnId === 'status') return statusFilterValues(this.$route.name, this.baseQuery)
      if (columnId === 'source_code') {
        // The codes the column offers, which is what the filter can be holding - a code it never
        // offers is one the user cannot have ticked, so the query must not ask for it either
        return offeredSourceCodes((this.$store.getters.getAllSourcecodes || [])
          .map(sourcecode => sourcecode && sourcecode.code))
      }
      if (columnId === 'bank') {
        return (this.$store.getters.getAllBanks || [])
          .map(bank => bank && bank.name)
          .filter(Boolean)
      }
      return []
    },

    onChangeVisibleColumns () {
      this.$nextTick(() => {
        this.gridApi.sizeColumnsToFit()
      })
    },
    /**
     * The part of the query the column filters do not own: the criteria of the view itself (tab
     * statuses, source code, ...) plus whatever else is applied, e.g. the current ordering.
     * @return {Object}
     */
    baseFilterQuery () {
      const storeFilters = Object.assign({}, this.getFilters)
      GRID_FILTER_PARAMS.forEach(param => delete storeFilters[param])
      return Object.assign(storeFilters, this.baseQuery)
    },
    /**
     * Looks up the ids of the store records a set filter selected by name/code, since the filter
     * holds what is displayed while the API expects ids
     * @param getter      Store getter returning the records
     * @param attribute   Record attribute the filter values were built from
     * @param values      Selected filter values
     * @return {Array}
     */
    idsForValues (getter, attribute, values) {
      return (this.$store.getters[getter] || [])
        .filter(record => record && values.includes(record[attribute]))
        .map(record => record.id)
    },
    /**
     * Turns an ag-grid date filter model into the API's range params. An 'equals' filter becomes a
     * range of one day, which is the only other criteria the date filters offer server side.
     * @param field   Name of the date field on the API
     * @param model   ag-grid date filter model
     * @return {Object}
     */
    dateRangeParams (field, model) {
      // ag-grid hands its dates over as 'YYYY-MM-DD HH:mm:ss', of which only the day is asked for
      const asDay = (value) => (typeof value === 'string' && value ? moment(value.split(' ')[0], 'YYYY-MM-DD', true) : null)
      const from = asDay(model.dateFrom)
      if (!(from && from.isValid())) return {}
      const to = asDay(model.dateTo)
      // The day itself, as the API reads it - a timestamp would carry this browser's offset into it
      return {
        [`${field}__gte`]: from.format('YYYY-MM-DD'),
        [`${field}__lte`]: (to && to.isValid() ? to : from).format('YYYY-MM-DD')
      }
    },
    /**
     * A filter model as one string, for telling whether the criteria have actually changed. The
     * grid and the seeding build their entries in a different key order, which a plain stringify
     * would read as a change - and refilter on criteria the rows on screen already answer to.
     * @param filterModel   ag-grid filter model
     * @return {string}
     */
    filterModelKey (filterModel) {
      const model = filterModel || {}
      return JSON.stringify(Object.keys(model).sort().map(columnId => {
        const criteria = model[columnId] || {}
        return [columnId, Object.keys(criteria).sort().map(key => [key, criteria[key]])]
      }))
    },
    /**
     * The filter model without the criteria that select nothing. A set filter with everything
     * unselected holds no criteria of its own - the query falls back to the view's - and it is a
     * state the grid drops on its own as soon as the rows are replaced. Leaving it out keeps the
     * grid, the query and the restore from disagreeing over it and refiltering each other.
     * @param filterModel   ag-grid filter model
     * @return {Object}
     */
    activeFilterModel (filterModel) {
      const active = {}
      Object.keys(filterModel || {}).forEach(columnId => {
        const model = filterModel[columnId]
        if (!model) return
        if (Array.isArray(model.values) && !model.values.length) return
        active[columnId] = model
      })
      return active
    },
    /**
     * Builds the API query for the current column filters
     * @param filterModel   ag-grid filter model
     * @param page          Page to request
     * @return {Object}
     */
    buildFilterQuery (filterModel = {}, page = 1, extendedFilters = {}, viewCriteriaOnly = false) {
      let query = Object.assign(this.baseFilterQuery(), { page })
      query.nest_lines = false

      Object.entries(filterModel || {}).forEach(([columnId, model]) => {
        if (!model) return
        const values = Array.isArray(model.values) ? pickedValues(model.values) : []
        const textValue = typeof model.filter === 'string' ? model.filter.trim() : ''

        switch (columnId) {
          case 'id': {
            // PO ids are matched as ids rather than searched for, several if a list is typed.
            // Entries may carry a "PO" prefix (e.g. "PO123"), so strip everything but the digits
            // and drop any entry left without an integer.
            const ids = textValue
              .split(/[\s,]+/)
              .map(id => id.replace(/\D/g, ''))
              .filter(Boolean)
            if (ids.length) query.id__in = ids
            break
          }
          case 'ledger': {
            // Ledger ids are matched as ids rather than searched for, several if a list is typed
            const ledgers = textValue.split(/[\s,]+/).filter(Boolean)
            if (ledgers.length) query.ledger__in = ledgers
            break
          }
          case 'number': {
            // An emptied box is asking for nothing, so the param is left off altogether
            const number = textValue.split(/[\s,]+/).filter(Boolean)
            if (number.length) query.number__icontains = number
            break
          }
          case 'vendor':
            // The vendor filter searches the API, so its model already holds vendor ids
            if (values.length) query.vendor__in = values
            break
          case 'description':
            if (textValue) query.description__icontains = textValue
            break
          case 'currency':
            if (values.length) query.batch__bank__currency__in = this.idsForValues('getAllCurrencies', 'code', values)
            break
          case 'amount_decimal':
            if (textValue) query.amount_decimal__icontains = textValue
            break
          case 'payment_type':
            if (values.length) query.payment_type__in = values
            break
          case 'payment_confirmation_number': {
            const paymentNumber = textValue.split(/[\s,]+/).filter(Boolean)
            if (paymentNumber.length) query.payment_confirmation_number__icontains = paymentNumber
            break
          }
          case 'payment_processed':
            // Picked as Cleared/Pending, sent as the flag behind them. Picking both says nothing
            // about the rows wanted, so it is left off the query.
            const flags = values.map(value => PAYMENT_STATUS_VALUES[value]).filter(flag => flag !== undefined)
            if (flags.length === 1) query.payment_processed = flags[0]
            break
          case 'batch':
            // The batch filter searches the API, so its model already holds batch ids
            if (values.length) query.batch__in = values
            break
          case 'status':
            if (values.length) query.status__in = values
            break     // Read from the selection below, since every status ticked leaves no model
          case 'source_code':
            break     // Read from the selection below, since every source code ticked leaves no model
          case 'date':
            Object.assign(query, this.dateRangeParams('date', model))
            break
          default:
            // The remaining columns are text filters, all served by the keyword search
            if (textValue) query.search = textValue
        }
      })

      /**
       * Ticking every status is asking for all of them, including the one the view leaves out by
       * default - and that is a filter with no model, so the statuses are read from the selection.
       * Whatever it holds is what the query asks for, VOIDED included or not, whichever way the
       * source code filter left it.
       *
       * Except where the filters were just started over: the tab's own statuses are what the view
       * is asking for then, and they are already in the query from its criteria. Reading the
       * filters at that point would read the tab that was left, since a set filter is only cleared
       * once ag-grid has run its values callback - which it does on a timer.
       */
      if (!viewCriteriaOnly) {
        const statuses = pickedValues(this.filterSelection(filterModel, 'status'))
        if (statuses.length) query.status__in = statuses

        // The source codes are read from the selection for the same reason, and asked for by their
        // code - the only param they are asked for by, from the first load on
        const sourceCodes = pickedValues(this.filterSelection(filterModel, 'source_code'))
        if (sourceCodes.length) query.source_code__code__in = sourceCodes
      }

      if (extendedFilters && extendedFilters.search) {
        query.search = extendedFilters.search
      }

      return query
    },
    /**
     * The values a set filter is asking for, or null on the views without the column. A set filter
     * with everything ticked is not an active filter, so it is absent from the model - and absent
     * means every value it offers, which is not the same as the view's own criteria.
     * @param filterModel
     * @param columnId
     * @return {Array|null}
     */
    filterSelection (filterModel, columnId) {
      if (!this.columnApi || !this.columnApi.getColumn(columnId)) return null
      const model = filterModel && filterModel[columnId]
      if (model && Array.isArray(model.values)) return model.values
      /**
       * With no model to read, the filter itself is asked what it is showing, since an absent model
       * is not one thing: every value ticked leaves none behind, and neither does a filter whose
       * values have not arrived yet - and those two say the opposite of each other. What the filter
       * holds is what the rows on screen answer to, so it is what the query is built from.
       */
      const ticked = this.tickedValues(columnId)
      if (ticked) return ticked
      // Nothing to read either way, so the defaults it is about to open on are what the view asks
      // for - never every value, which is what the filter would read as once it is built
      return this.defaultFilterSelection(columnId, filterModel) || this.filterValues(columnId)
    },
    /**
     * The value model of a set filter once it has been handed its values, which ag-grid does on a
     * timer, or null until then - before which the filter is showing nothing at all
     * @param columnId
     * @return {Object|null}
     */
    setFilterValueModel (columnId) {
      const filter = this.gridApi && this.gridApi.getFilterInstance(columnId)
      const valueModel = (filter && typeof filter.getValueModel === 'function') ? filter.getValueModel() : null
      return (valueModel && valueModel.getValues().length) ? valueModel : null
    },
    /**
     * The values a set filter is showing as ticked, or null while it has not been given its values
     * @param columnId
     * @return {Array|null}
     */
    tickedValues (columnId) {
      const valueModel = this.setFilterValueModel(columnId)
      if (!valueModel) return null
      const filter = this.gridApi && this.gridApi.getFilterInstance(columnId)
      const model = (filter && typeof filter.getModel === 'function') ? filter.getModel() : null
      // A null set-filter model is ag-grid's "everything selected" — every offered value is ticked.
      if (!model || !Array.isArray(model.values)) return this.filterValues(columnId)
      const ticked = new Set(model.values)
      return this.filterValues(columnId).filter(value => ticked.has(value))
    },
    /**
     * Applies the query to the store filters. SET_FILTERS merges, so criteria the user just cleared
     * have to be unset explicitly or they would survive in the next request.
     * @param query
     */
    async syncStoreFilters (query) {
      const cleared = GRID_FILTER_PARAMS.filter(param => !query.hasOwnProperty(param))
      for (const param of cleared) {
        await this.$store.dispatch('unsetFilter', param)
      }
      // A copy, since the fetch actions add their own params to the query they are given
      await this.$store.dispatch('setFilter', Object.assign({}, query))
    },
    /**
     * Fetches the rows matching the query. The results go through the store, which is what feeds
     * the 'rows' prop and the record count the pagination is built from.
     * @param query
     * @param filterModel   The criteria the query was built from, put back if the refresh loses them
     */
    async fetchFilteredRows (query, filterModel = null) {
      const config = this.serverFilterConfig
      if (!config) return
      // What the rows about to be fetched reflect, so the same criteria are not filtered on twice
      this.lastFilterModel = this.filterModelKey(filterModel)
      this.$store.commit('SET_FILTER_STATUS', true)
      this.gridApi.showLoadingOverlay()
      try {
        await this.syncStoreFilters(query)
        // Only the views that remember their filters between visits have somewhere to put them
        if (config.filterInfoAction) {
          await this.$store.dispatch(config.filterInfoAction, Object.assign({}, query))
        }
        await Promise.all([
          this.$store.dispatch(config.resetMetaAction),
          this.$store.dispatch(config.clearAction, true)
        ])
        this.clearSelection()
        // Filtering starts from the first page again, which the view showing the rows has to know
        this.$emit('filtersApplied', query.page || 1)
        const payload = config.passQueryDirectly ? query : { query, storeLines: false }
        const response = await this.$store.dispatch(config.fetchAction, payload)
        if (response) {
          // const promises = await this.$store.dispatch('findDependencies', response.entities)
          await Promise.all([response])
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.$store.commit('SET_FILTER_STATUS', false)
        this.restoreFilterModel(filterModel)
      }
    },
    /**
     * Puts the criteria back into the filters if the refresh left the grid without them, so the
     * text box still shows what was searched for. Nothing is filtered again: the rows on screen are
     * the answer to these very criteria.
     * @param filterModel
     */
    restoreFilterModel (filterModel) {
      if (!(filterModel && Object.keys(filterModel).length) || !this.gridApi) return
      const current = this.activeFilterModel(this.gridApi.getFilterModel())
      // A column keeps its criteria whichever filter triggered the refresh: what was typed into the
      // text filters is how the user reads back what they searched for
      const dropped = Object.keys(filterModel).some(columnId => !current[columnId])
      if (!dropped) return
      // The model the rows already answer to, so putting it back does not filter again
      this.setFilterModelQuietly(filterModel)
    },
    /**
     * Drops the selection, which belongs to the result set being replaced
     */
    clearSelection () {
      if (this.emitSelect) {
        this.$emit('onSelect', [])
      } else {
        this.$store.dispatch('updateTransactionSelection', [])
      }
    },
    /**
     * Clears the column filters without fetching, for when the view reloads itself with its own
     * criteria - a tab change starts from a clean set of filters
     */
    resetColumnFilters () {
      if (!this.gridApi) return
      // The view is refetching on its own criteria, so nothing filtered before still holds
      this.lastFilterModel = null
      clearFilterModel(this.$route.name)
      /**
       * The load that follows asks with the view's own criteria - the tab's statuses - and nothing
       * of the filters at all. They have just been cleared, and a set filter is only cleared once
       * its values callback has run, which ag-grid does on a timer: read before then, the grid and
       * the filters both still hold the tab that was left.
       */
      this.filtersStartedOver = true
      this.setFilterModelQuietly(null)
      /**
       * A set filter is handed its values by a callback ag-grid runs when the filter is built and
       * when its popup is opened, and at no other time - so after a tab change it would still be
       * offering the statuses of the tab that was left, and a cleared filter reads as asking for
       * every value it offers. Re-reading them leaves it on all of the new tab's instead.
       */
      SEEDED_FILTER_COLUMNS.forEach(columnId => this.refreshFilterValues(columnId))
    },
    /**
     * Has a set filter re-read the values it offers, for when the view's criteria have moved on.
     * Nothing is filtered by it: the values are what the filter can be asked for, not what it is
     * asking for, and ag-grid leaves the selection alone where the new values allow.
     * @param columnId
     */
    refreshFilterValues (columnId) {
      const filter = this.gridApi && this.gridApi.getFilterInstance(columnId)
      if (filter && typeof filter.refreshFilterValues === 'function') filter.refreshFilterValues()
    },
    onFilterChanged (params) {
      if (!params || !this.gridApi) return
      // A write the grid was given coming back, e.g. the defaults the view seeded reaching the
      // filters once their values arrive. The rows on screen are the answer to it already.
      if (this.quietFilterWrites > 0) {
        this.quietFilterWrites -= 1
        return
      }
      if (!this.serverFilterConfig) return    // Views that still filter client side keep doing so
      // this.$store.dispatch('unsetFilter', 'search')
      const filterModel = this.activeFilterModel(this.gridApi.getFilterModel())
      // Criteria the results already reflect, e.g. put back after a refresh so the user can still
      // read what they searched for, or the seeded defaults reaching the filters once their values
      // arrive. Filtering on them again would only fetch the same rows.
      if (this.filterModelKey(filterModel) === this.lastFilterModel) return
      this.gridApi.showLoadingOverlay()
      if (this.filterDebounceTimer) {
        clearTimeout(this.filterDebounceTimer)
      }
      // Filtering changes the size of the result set, so go back to the first page. What the view
      // itself is asking for - its keyword search - still holds. The status filter is only followed
      // on once the grid is done with this change, since following it puts a model back in.
      this.filterDebounceTimer = setTimeout(() => {
        const model = this.syncVoidedStatus(filterModel)
        this.fetchFilteredRows(this.buildFilterQuery(model, 1, this.extendedFilters), model)
      }, 250)
    },
    exportToExcel () {
      this.gridApi.exportDataAsExcel()
    },
    updateSelection (gridApi) {
      const api = gridApi || this.gridApi
      if (!api) return
      let selected = []
      api.forEachNode((row) => {
        if (row && row.selected && row.data) {
          selected.push(row.data)
        }
      })
      if (this.emitSelect) {
        this.$emit('onSelect', selected)
      } else {
        let notThisPage = this.selection.filter(v => v.page !== this.currentPage)
        this.$store.dispatch('updateTransactionSelection', [...notThisPage, ...selected])
      }
    },
    prepareColumns (columns) {
      this.actionsColumn.cellRendererParams = {
        clickhandleVoidDialogVisible: this.clickhandleVoidDialogVisible,
        loadingRelated: this.loadingRelated
      }
      if (columns && columns.length) {
        return this.aggridColumns.map((c) => this[c])
      } else {
        return [
          this.rowSelect,
          this.ledgerColumn,
          this.numberColumn,
          this.vendorColumn,
          this.descriptionColumn,
          this.amountColumn,
          this.batchColumn,
          this.actionsColumn,
          this.dueDateColumn
        ]
      }
    },
    deselectAll () {
      this.gridApi.deselectAll()
    },
    clickhandleVoidDialogVisible (data) {
      this.$emit('handleVoidDialogVisible', data)
    },
    getLoading () {
      return this.loadingRelated
    },
    restoreSelection (data, key = 'id', page = this.currentPage) {
      if (!(data && data.length) || !this.gridApi) return
      const thisPageSelectedIds = data
        .filter(v => v.page === page)
        .map(v => v[key])
      // ag-grid v31: use the gridApi captured in onGridReady (gridOptions.api is no longer populated).
      this.gridApi.forEachNode(node => node.setSelected(thisPageSelectedIds.includes(node.data[key])))
    }
  },
  beforeMount () {
    this.defaultColDefs = this.prepareColumns(this.aggridColumns)
    this.defaultGridOptions = {
      // v31: fit columns to the grid width on initial render. safeSizeColumnsToFit runs from
      // onFirstDataRendered, which ag-grid does NOT fire for an empty/filtered result set, so an empty grid
      // kept its defined column widths and left blank space to the right of the last column ("empty column
      // beside Status"). fitGridWidth sizes columns on first render regardless of row data.
      autoSizeStrategy: { type: 'fitGridWidth' },
      singleClickEdit: true,
      undoRedoCellEditing: false,
      enterNavigatesVertically: true,
      enterNavigatesVerticallyAfterEdit: true,
      rowSelection: 'multiple',
      // v31: stable row identity so the grid delta-updates instead of fully swapping the row model on a
      // refetch (which widened the empty-state window behind the "No rows" overlay after posting a payment).
      getRowId: (params) => (params.data && params.data.id != null ? String(params.data.id) : undefined),
      defaultColDef: {
        resizable: true,
        menuTabs: [],
        suppressSizeToFit: false
      },
      pinnedBottomRowData: [],
      /**
       * Filter popups hang off the body rather than the grid. The grid sizes itself to its rows and
       * clips whatever overflows it, so on an empty result set a popup taller than the header - the
       * currency filter and its Apply/Cancel buttons, say - would be cut off.
       */
      popupParent: document.body,
      enableRangeSelection: true,
      animateRows: true,
      suppressRowClickSelection: true,
      suppressCellFocus: false,
      // Classes and events
      rowClassRules: {
        'ag-error': (params) => params.node.aggData && !this.hasCompleteAchData(params.node.aggData.vendorResource)
      },
      onGridReady: (params) => this.onGridReady(params),
      onFirstDataRendered: (params) => this.onFirstDataRendered(params),
      onSelectionChanged: (params) => {
        this.updateSelection(params.api)
      },
      onFilterChanged: (params) => {
        this.onFilterChanged(params)
      },
      getContextMenuItems: (params) => {
        const defaultItems = ['copy', 'copyWithHeaders', 'separator', 'export']
        if (params.column && (params.column.getColId() === 'number' || params.column.getColId() === 'ledger')) {
          defaultItems.unshift({
            name: 'Open in New Tab',
            action: () => {
              const index = this.$route.path.lastIndexOf('/')
              const path = `${this.$route.path}${index + 1 !== this.$route.path.length ? '/' : ''}${params.node.data.id}`
              const url = path
              if (url) {
                window.open(url, '_blank')
              }
            },
            icon: '<span class="ag-icon ag-icon-link"></span>'
          })
        }
        return defaultItems
      }
    }
    this.components = {
      agGridBtnRender: AgGridBtnRender,
      // The vendor and batch columns search their API instead of listing what is already loaded
      agGridSearchFilter: AgGridSearchFilter,
      agGridSearchFloatingFilter: AgGridSearchFloatingFilter,
      // The text columns are filtered on a button rather than as the user types
      agGridTextFloatingFilter: AgGridTextFloatingFilter
    }
  },
  /**
   * Lets go of everything that would outlive this grid. The click listener is on an element the
   * grid does not own the lifetime of, and its handler holds this component - and through it the
   * grid api, the column defs and the store - so leaving it attached keeps the whole lot in memory
   * for every list view visited.
   */
  beforeUnmount () {
    if (this.$el) {
      this.$el.removeEventListener('click', this.handleGridClick)
    }
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer)
      this.filterDebounceTimer = null
    }
    this.gridApi = null
    this.columnApi = null
  },
  watch: {
    aggridColumns (val) {
      // v31: single setGridOption('columnDefs') call — the old setColumnDefs([]) reset dropped column state.
      this.setGridColumns(this.prepareColumns(val))
    },
    rows () {
      // The values a filter is seeded from may only have arrived with these rows
      if (!this.defaultFiltersSeeded) this.seedDefaultFilters()
      if (!this.gridApi) return
      this.$nextTick(() => {
        var hasRows = !!(this.rows && this.rows.length)
        if (this.isFilterLoading && !hasRows) {
          this.gridApi.showLoadingOverlay()
          return
        }
        // Push rows to the grid safely
        this.setGridRows(this.rows)
        
        // If data returned empty and we are NOT loading, make sure the grid shows the overlay
        hasRows = !!(this.rows && this.rows.length)
        if (!this.isFilterLoading && !hasRows) {
          this.gridApi.showNoRowsOverlay()
        } else if (hasRows) {
          this.gridApi.hideOverlay()
        }
      })
    },
    selection (val) {
      if (this.gridApi) {
        const selectedData = this.gridApi.getSelectedRows()
        if (!(val && val.length) && selectedData && selectedData.length) {
          this.gridApi.deselectAll()
        }
      }
    },
    isFilterLoading (value) {
      if (!this.gridApi) return
      this.$nextTick(() => {
        if (value) {
          this.gridApi.showLoadingOverlay()
          return
        }
        const hasRows = !!(this.rows && this.rows.length)
        this.setGridRows(this.rows)
        if (hasRows) {
          this.gridApi.hideOverlay()
        } else {
          this.gridApi.showNoRowsOverlay()
        }
      })
    },
  }
})
</script>

<template>
  <ag-grid-vue
    :name="name"
    :ref="name"
    :class="classes"
    :rowHeight="rowHeight"
    :style="{width, margin: '17px'}"
    :column-defs="columnDefs"
    :key="iteration"
    :grid-options="gridOptions"
    :dom-layout="domLayout"
    :components="components"
  />
</template>
<style scoped>
.ag-theme-balham .ag-row .ag-cell {
  display: flex !important;
  justify-content: center !important; /* align horizontal */
  align-items: center !important;
}
</style>

<!--
  The filter popups are appended to the body rather than to this component, so scoped rules never
  reach them. ag-grid builds the Apply/Cancel buttons itself, which leaves styling them by their
  own classes as the way to make them read as the element-ui buttons used everywhere else.
-->
<style>
  .ag-filter-apply-panel .ag-standard-button {
    -webkit-appearance: none;
    appearance: none;
    outline: 0;
    display: inline-block;
    box-sizing: border-box;
    margin: 0;
    padding: 7px 9px;
    border: 1px solid #c4c4c4;
    border-radius: 4px;
    background: #fff;
    color: #1f2d3d;
    font-size: 12px;
    line-height: 1;
    text-align: center;
    white-space: nowrap;
    cursor: pointer;
  }
  .ag-filter-apply-panel .ag-standard-button + .ag-standard-button {
    margin-left: 8px;
  }
  .ag-filter-apply-panel .ag-standard-button:hover,
  .ag-filter-apply-panel .ag-standard-button:focus {
    color: #20a0ff;
    border-color: #20a0ff;
  }
  .ag-filter-apply-panel .ag-standard-button:active {
    color: #1d90e6;
    border-color: #1d90e6;
    outline: 0;
  }
  /* Applying is the primary action, the way it is in the dialogs */
  .ag-filter-apply-panel [ref="applyFilterButton"] {
    background-color: #20a0ff;
    border-color: #20a0ff;
    color: #fff;
  }
  .ag-filter-apply-panel [ref="applyFilterButton"]:hover,
  .ag-filter-apply-panel [ref="applyFilterButton"]:focus {
    background: #4db3ff;
    border-color: #4db3ff;
    color: #fff;
  }
  .ag-filter-apply-panel [ref="applyFilterButton"]:active {
    background: #1d90e6;
    border-color: #1d90e6;
    color: #fff;
  }
</style>
